import { useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { SpotlightTourProvider, useSpotlightTour } from 'react-native-spotlight-tour';
import type { TourStep } from 'react-native-spotlight-tour';
import {
  TOUR_OVERLAY_COLOR,
  TOUR_OVERLAY_OPACITY,
  TOUR_SPOTLIGHT_PADDING,
  TOUR_VIEWPORT_PADDING,
} from '../constants';
import { useOnboarding } from '../contexts/OnboardingContext';
import { prepareTourAnchor } from '../utils/scroll';
import { TourTooltip } from './TourTooltip';

const TourController = () => {
  const { activeTour, steps } = useOnboarding();
  const { start, stop, status } = useSpotlightTour();
  const playing = useRef<string | null>(null);

  useEffect(() => {
    const tourId = activeTour?.id ?? null;

    if (tourId && steps.length && playing.current !== tourId) {
      playing.current = tourId;
      start();
      return;
    }

    if (!tourId && playing.current) {
      playing.current = null;

      if (status === 'running') {
        stop();
      }
    }
  }, [activeTour, steps, start, stop, status]);

  return null;
};

export const OnboardingTourHost = ({ children }: { children: ReactNode }) => {
  const { steps, endTour } = useOnboarding();

  const tourSteps = useMemo<TourStep[]>(
    () =>
      steps.map(step => ({
        before: () => prepareTourAnchor(step.target, step.placement),
        placement: step.placement,
        shape: { padding: TOUR_SPOTLIGHT_PADDING, type: 'rectangle' },
        shift: { crossAxis: true, padding: TOUR_VIEWPORT_PADDING },
        onBackdropPress: () => undefined,
        render: ({ current, isLast, next, stop }) => (
          <TourTooltip
            step={step}
            stepIndex={current}
            stepCount={steps.length}
            isLast={isLast}
            onNext={next}
            onClose={stop}
          />
        ),
      })),
    [steps],
  );

  const handleStop = ({ isLast }: { isLast: boolean }) =>
    endTour(isLast ? 'completed' : 'skipped');

  return (
    <SpotlightTourProvider
      steps={tourSteps}
      overlayColor={TOUR_OVERLAY_COLOR}
      overlayOpacity={TOUR_OVERLAY_OPACITY}
      motion="fade"
      nativeDriver
      onStop={handleStop}
    >
      <TourController />
      {children}
    </SpotlightTourProvider>
  );
};

export default OnboardingTourHost;
